import { Box, Typography, Chip } from "@mui/material";
import { useContext } from "react";
import { MainContext } from "../../../../main/context/MainContextProvider";
import { PVSystExpectedLosses } from "../../types";
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';

export type PVSystComparisonCardProps = {
    pvsystReference: PVSystExpectedLosses;
    realOptical: number;
    realConversion: number;
    realInverter: number;
    realTransformer: number;
    idealEnergy: number;
};

export default function PVSystComparisonCard({
    pvsystReference,
    realOptical,
    realConversion,
    realInverter,
    realTransformer,
    idealEnergy
}: PVSystComparisonCardProps) {
    const { theme } = useContext(MainContext);

    // Calcular percentuais reais
    const realOpticalPercent = idealEnergy > 0 ? (realOptical / idealEnergy) * 100 : 0;
    const realConversionPercent = idealEnergy > 0 ? (realConversion / idealEnergy) * 100 : 0;
    const realInverterPercent = idealEnergy > 0 ? (realInverter / idealEnergy) * 100 : 0;
    const realTransformerPercent = idealEnergy > 0 ? (realTransformer / idealEnergy) * 100 : 0;

    const categories = [
        {
            name: "Óptica e Irradiação",
            pvsyst: pvsystReference.opticalLossPercentage,
            real: realOpticalPercent,
            value: realOptical
        },
        {
            name: "Conversão e Módulo DC",
            pvsyst: pvsystReference.conversionLossPercentage,
            real: realConversionPercent,
            value: realConversion
        },
        {
            name: "Eficiência Inversor",
            pvsyst: pvsystReference.inverterLossPercentage,
            real: realInverterPercent,
            value: realInverter
        },
        {
            name: "Eficiência Transformador",
            pvsyst: pvsystReference.transformerLossPercentage,
            real: realTransformerPercent,
            value: realTransformer
        }
    ];

    return (
        <Box
            sx={{
                backgroundColor: theme.cardBoxBackgroudColor || theme.backgroundColor,
                borderRadius: "12px",
                padding: "16px",
                border: `1px solid ${theme.cardBorderColor}`
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "16px" }}>
                <CompareArrowsIcon sx={{ color: "#8B5CF6", fontSize: 22 }} />
                <Typography sx={{ fontSize: "14px", fontWeight: "500", color: theme.primaryTextColor }}>
                    Comparação PVSyst vs Real
                </Typography>
            </Box>

            {categories.map((cat, index) => {
                const diff = cat.real - cat.pvsyst;
                const isAbove = diff > 0.5; // Mais de 0.5% acima
                const isBelow = diff < -0.5; // Mais de 0.5% abaixo
                const isNormal = !isAbove && !isBelow;

                return (
                    <Box
                        key={index}
                        sx={{
                            marginBottom: "12px",
                            paddingBottom: "12px",
                            borderBottom: index < categories.length - 1 ? `1px solid ${theme.cardBorderColor}` : "none"
                        }}
                    >
                        <Typography sx={{ fontSize: "12px", fontWeight: "500", color: theme.primaryTextColor, marginBottom: "8px" }}>
                            {cat.name}
                        </Typography>
                        
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "4px" }}>
                            <Typography sx={{ fontSize: "11px", color: theme.secondaryTextColor }}>
                                PVSyst: {cat.pvsyst.toFixed(2)}%
                            </Typography>
                            <Typography sx={{ fontSize: "11px", color: theme.secondaryTextColor }}>
                                Real: {cat.real.toFixed(2)}% ({cat.value.toFixed(1)} MWh)
                            </Typography>
                        </Box>

                        <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <Box
                                sx={{
                                    flex: 1,
                                    height: "6px",
                                    backgroundColor: theme.cardBorderColor || "rgba(255,255,255,0.1)",
                                    borderRadius: "3px",
                                    position: "relative",
                                    overflow: "hidden"
                                }}
                            >
                                <Box
                                    sx={{
                                        position: "absolute",
                                        left: 0,
                                        top: 0,
                                        height: "100%",
                                        width: `${Math.min(cat.real, 100)}%`,
                                        backgroundColor: isAbove ? "#EF4444" : isBelow ? "#10B981" : "#3B82F6",
                                        borderRadius: "3px"
                                    }}
                                />
                            </Box>
                            {isAbove && (
                                <Chip
                                    label={`+${diff.toFixed(1)}%`}
                                    size="small"
                                    sx={{
                                        backgroundColor: "rgba(239, 68, 68, 0.15)",
                                        color: "#EF4444",
                                        fontSize: "10px",
                                        height: "20px"
                                    }}
                                />
                            )}
                            {isBelow && (
                                <Chip
                                    label={`${diff.toFixed(1)}%`}
                                    size="small"
                                    sx={{
                                        backgroundColor: "rgba(16, 185, 129, 0.15)",
                                        color: "#10B981",
                                        fontSize: "10px",
                                        height: "20px"
                                    }}
                                />
                            )}
                            {isNormal && (
                                <Chip
                                    label="OK"
                                    size="small"
                                    sx={{
                                        backgroundColor: "rgba(59, 130, 246, 0.15)",
                                        color: "#3B82F6",
                                        fontSize: "10px",
                                        height: "20px"
                                    }}
                                />
                            )}
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );
}
